import React, { useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import ModalLog from "./ModalLog";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  useDisclosure,
  Stack,
  Text,
  Container,
  Divider,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";

// ─── Nav items defined once ──────────────────────────────────
const NAV_ITEMS = [
  { to: "/", label: "Home", emoji: "🏠" },
  { to: "/stocks", label: "Stocks", emoji: "📈" },
  { to: "/milestones", label: "Milestones", emoji: "🎯" },
  { to: "/growth_tracker", label: "Growth", emoji: "🌱" },
  { to: "/my_toolkit", label: "Toolkit", emoji: "🛠️" },
  { to: "/about", label: "About", emoji: "ℹ️" },
];

// ─── NavLink ─────────────────────────────────────────────────
const NavLink = ({ to, children, onClick }) => {
  const { pathname } = useLocation();
  const isActive = pathname === to;

  return (
    <Box
      as={RouterLink}
      to={to}
      onClick={onClick}
      px={3}
      py={2}
      rounded="lg"
      fontSize="sm"
      fontWeight="semibold"
      color={isActive ? "teal.300" : "gray.300"}
      bg={isActive ? "rgba(56, 178, 172, 0.1)" : "transparent"}
      borderWidth="1px"
      borderColor={isActive ? "teal.500" : "transparent"}
      transition="all 0.2s"
      _hover={{
        textDecoration: "none",
        color: "teal.200",
        bg: "rgba(56, 178, 172, 0.15)",
        borderColor: "teal.400",
        transform: "translateY(-1px)",
      }}
    >
      {children}
    </Box>
  );
};

// ─── Header ──────────────────────────────────────────────────
export default function Header() {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  return (
    <Box
      bg="linear-gradient(180deg, rgba(26,32,44,0.98), rgba(17,21,28,0.98))"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
      boxShadow="0 2px 10px rgba(0, 0, 0, 0.3)"
      position="sticky"
      top={0}
      zIndex={1000}
      backdropFilter="blur(12px)"
    >
      <Container maxW="1400px" px={{ base: 3, md: 4 }}>
        <Flex
          h={{ base: 14, md: 16 }}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Mobile Menu Button */}
          <IconButton
            size="sm"
            icon={
              isOpen ? (
                <CloseIcon boxSize={3} />
              ) : (
                <HamburgerIcon boxSize={4} />
              )
            }
            aria-label="Toggle Menu"
            display={{ md: "none" }}
            onClick={onToggle}
            bg="whiteAlpha.100"
            color="teal.300"
            _hover={{ bg: "whiteAlpha.200" }}
            borderRadius="lg"
          />

          {/* Logo */}
          <HStack spacing={{ base: 4, md: 8 }} alignItems="center" flex={1}>
            <Box
              as={RouterLink}
              to="/"
              display="flex"
              alignItems="center"
              gap={2}
              _hover={{ textDecoration: "none" }}
              transition="opacity 0.2s"
              ml={{ base: 3, md: 0 }}
            >
              <Text
                bgGradient="linear(to-r, teal.300, cyan.400)"
                bgClip="text"
                fontWeight="extrabold"
                fontSize={{ base: "md", md: "lg" }}
              >
                Mattias Assets
              </Text>
              <Text
                color="gray.500"
                display={{ base: "none", lg: "inline" }}
                fontSize="sm"
              >
                Portfolio
              </Text>
            </Box>

            {/* Desktop Navigation */}
            <HStack as="nav" spacing={1.5} display={{ base: "none", md: "flex" }}>
              {NAV_ITEMS.map(({ to, label, emoji }) => (
                <NavLink key={to} to={to}>
                  {emoji} {label}
                </NavLink>
              ))}
            </HStack>
          </HStack>

          {/* Right side */}
          <Flex alignItems="center">
            <ModalLog />
          </Flex>
        </Flex>

        {/* Mobile Navigation */}
        {isOpen && (
          <Box
            pb={3}
            display={{ md: "none" }}
            sx={{
              animation: "slideDown 0.2s ease-out",
              "@keyframes slideDown": {
                from: { opacity: 0, transform: "translateY(-8px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Divider borderColor="whiteAlpha.100" mb={3} />
            <Stack as="nav" spacing={1}>
              {NAV_ITEMS.map(({ to, label, emoji }) => (
                <NavLink key={to} to={to} onClick={onClose}>
                  {emoji} {label}
                </NavLink>
              ))}
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}